// @flow

import assert from 'assert';

export type JsonObject = { [string]: string };

export type State = Map<string, string>;

export type StorageEvent = {
  key: string,
  oldValue?: string | null,
  newValue?: string | null
};

export interface SettingsStorageInterface {
  getItem(key: string): ?string,
  setItem(key: string, value: string, shouldNotify: boolean): void,
  removeItem(key: string, shouldNotify: boolean): void,
  key(index: number): string,
  clear(shouldNotify: boolean): void,
  length(): number,
  _toJS(): JsonObject
}

const createStateFromObj = (obj: JsonObject, state = new Map()): State => {
  Object.keys(obj).forEach((key: string): void => {
    state.set(key, obj[key]);
  });
  return state;
};

export default (
  initialState: JsonObject,
  notify: (string, ?StorageEvent) => void
): SettingsStorageInterface => {
  // We want to hide state from developers
  const STATE = createStateFromObj(initialState);

  class SettingsStorage implements SettingsStorageInterface {
    updateState(newState: JsonObject) {
      STATE.clear();
      createStateFromObj(newState, STATE);
    }

    getItem(key: string) {
      return STATE.get(key);
    }

    setItem(key: string, value: string, shouldNotify: boolean = true): void {
      assert(typeof value === 'string', `Item value must be a string, current type of value is ${typeof value}`);

      const event: StorageEvent = {
        key,
        oldValue: this.getItem(key),
        newValue: value
      };

      STATE.set(key, value);

      if (shouldNotify) {
        notify('set', event);
      }
    }

    removeItem(key: string, shouldNotify: boolean = true): void {
      const event: StorageEvent = {
        key,
        oldValue: this.getItem(key)
      };

      STATE.delete(key);

      if (shouldNotify) {
        notify('remove', event);
      }
    }

    key(index: number): string {
      const keys = [...STATE.keys()];
      return keys[index];
    }

    clear(shouldNotify: boolean = true): void {
      STATE.clear();

      if (shouldNotify) {
        notify('clear');
      }
    }

    // $FlowFixMe - Doesn't support getters
    get length(): number {
      return STATE.size;
    }

    _toJS(): JsonObject {
      const jsObject = {};
      STATE.forEach((value: string, key: string): void => {
        jsObject[key] = value;
      });
      return jsObject;
    }
  }

  return new SettingsStorage();
};




// WEBPACK FOOTER //
// ./src/lib/settings-storage.js